import type { JSX } from "react";
import { useI18n } from "../../shared/i18n/I18nContext";
import { useGuardedSignOut, useSignOutHandover } from "../../shared/session/signOutRequest";
import type { SignOutRequest } from "../../shared/session/signOutRequest";
import { PageHeader } from "../../shared/ui/PageHeader";
import styles from "../../shared/ui/ui.module.css";
import { SettingsSections } from "./SettingsSections";

/** Properties of {@link SettingsContent}. */
interface SettingsContentProps {
  /** What the account menu or „Mehr“ handed over, or `null`. */
  readonly signOutRequest: SignOutRequest | null;
}

/**
 * The sections of „Einstellungen“ with the sign-out that starts from a handed-over request.
 *
 * @param props - Component properties.
 * @returns The sections.
 */
function SettingsContent(props: SettingsContentProps): JSX.Element {
  const signOut = useGuardedSignOut(props.signOutRequest);
  const sections = <SettingsSections preferencesTitleKey="account.preferences" signOut={signOut} />;

  return sections;
}

/**
 * „Einstellungen“, opened from the account menu in the app bar (F-74): the same settings as on
 * „Mehr“ without the list of areas, in every layout. On wide screens it replaces „Mehr“, whose
 * address leads here. A sign-out of the menu that needs a confirmation or failed arrives here with
 * the fragment `#account`, as does an open confirmation or error of „Mehr“ when the window becomes
 * wide (F-84); every such arrival starts the sections afresh, and the request is removed from the
 * history entry, so reloading shows it no more.
 *
 * @returns The page element.
 */
export function SettingsPage(): JSX.Element {
  const { t } = useI18n();
  const handover = useSignOutHandover();
  const page = (
    <div className={styles["page"]}>
      <PageHeader title={t("account.settings")} />
      <SettingsContent
        key={handover?.key ?? "settings"}
        signOutRequest={handover?.request ?? null}
      />
    </div>
  );

  return page;
}
